import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { PageBanner } from "@/components/ui/PageBanner";
import { TrainingProfile } from "@/components/ui/TrainingProfile";
import { useNavigate } from "react-router-dom";
import { IMG } from "@/lib/assets";
import { Plus, Scale, TrendingDown, Trophy, Dumbbell, Sparkles, Activity, Timer } from "lucide-react";

export default function Progress() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: series = [] } = useQuery({ queryKey: ["series", "weight_kg"], queryFn: async () => (await api.get("/api/progress/series", { params: { metric: "weight_kg" } })).data });
  const { data: logs = [] } = useQuery({ queryKey: ["progress"], queryFn: async () => (await api.get("/api/progress")).data });
  const { data: prs = [] } = useQuery({ queryKey: ["prs"], queryFn: async () => (await api.get("/api/logs/prs")).data });
  const { data: liftLogs = [] } = useQuery({ queryKey: ["liftlogs"], queryFn: async () => (await api.get("/api/logs")).data });

  const latest = series[series.length - 1]?.value;
  const first = series[0]?.value;
  const delta = latest != null && first != null ? (latest - first).toFixed(1) : null;
  const prCount = prs.filter((p) => p.best_est_1rm != null).length;

  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.progress} overline="telemetry" title="Progress & PRs"
        subtitle="Tell your OCTON coach what you lifted, by chat or live voice. It logs every rep, tracks your PRs and maps your strengths and weak points."
        actions={<AddMeasurement onDone={() => qc.invalidateQueries()} />} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Trophy} label="Movements tracked" value={prs.length} accent="#CCFF00" />
        <Stat icon={Dumbbell} label="Lifts logged" value={liftLogs.length} />
        <Stat icon={Scale} label="Current weight" value={latest != null ? `${latest} kg` : "–"} />
        <Stat icon={TrendingDown} label="Weight change" value={delta != null ? `${delta} kg` : "–"} accent={delta < 0 ? "#CCFF00" : "#FFB340"} />
      </div>

      <TrainingProfile onAskCoach={() => navigate("/app/coach?voice=1&topic=progress")} />

      {/* Personal records */}
      <div className="surface rounded-lg p-5" data-testid="pr-section">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-4 h-4 text-acid" />
          <span className="font-heading font-bold tracking-tight">Personal Records</span>
          <span className="mono-label ml-auto">{prCount} strength maxes</span>
        </div>
        {prs.length === 0 ? (
          <EmptyHint />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {prs.map((p) => <PrCard key={p.movement} pr={p} />)}
          </div>
        )}
      </div>

      {/* Movement trend */}
      <MovementTrend movements={prs} />

      {/* Areas for development */}
      <DevelopmentAreas hasLogs={prs.length > 0} />

      {/* Bodyweight chart */}
      <div className="surface rounded-lg p-5">
        <p className="mono-label mb-4">Bodyweight trajectory (kg)</p>
        {series.length > 1 ? (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ left: -18, right: 6, top: 6 }}>
                <defs>
                  <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#CCFF00" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#CCFF00" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1E222A" vertical={false} />
                <XAxis dataKey="date" stroke="#565C6F" fontSize={11} tickLine={false} />
                <YAxis stroke="#565C6F" fontSize={11} domain={["auto", "auto"]} tickLine={false} />
                <Tooltip contentStyle={{ background: "#0F1115", border: "1px solid #1E222A", borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="value" stroke="#CCFF00" strokeWidth={2} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : <p className="text-[#565C6F] text-sm py-8 text-center">Add measurements to plot your trajectory.</p>}
      </div>

      {/* Recent lifts */}
      <div className="surface rounded-lg p-5">
        <p className="mono-label mb-3">Recent lifts logged by OCTON</p>
        <div className="space-y-2">
          {liftLogs.slice(0, 15).map((l) => <LiftRow key={l.id} l={l} />)}
          {liftLogs.length === 0 && <p className="text-[#565C6F] text-sm">No lifts logged yet — tell OCTON what you trained.</p>}
        </div>
      </div>
    </div>
  );
}

function EmptyHint() {
  return (
    <div className="text-center py-8">
      <Sparkles className="w-6 h-6 text-[#565C6F] mx-auto mb-2" />
      <p className="text-[#8A91A6] text-sm max-w-md mx-auto">
        No records yet. Head to <span className="text-acid">OCTON Coach</span> and say something like
        <span className="text-white/90"> "I did 5x5 back squat at 100kg"</span> — by text or live voice — and OCTON logs it here automatically.
      </p>
    </div>
  );
}

function fmtTime(sec) {
  if (sec == null) return null;
  const s = Math.round(sec);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function PrCard({ pr }) {
  const strength = pr.best_est_1rm != null || pr.best_weight_kg != null;
  const timeBench = pr.best_time_sec != null;
  const amrap = pr.best_total_reps != null;
  return (
    <div className="rounded-md border border-[#1E222A] surface-elev p-3" data-testid="pr-card">
      <p className="text-sm font-medium truncate">{pr.movement}</p>
      <p className="mono-label mt-0.5 uppercase" style={{ color: "#565C6F" }}>{pr.category}</p>
      {strength ? (
        <>
          <p className="font-heading font-black text-2xl text-acid mt-2">{pr.best_weight_kg ?? "–"}<span className="text-sm text-[#8A91A6] ml-1">kg</span></p>
          {pr.best_est_1rm != null && <p className="mono-label mt-1">est 1RM {pr.best_est_1rm}kg</p>}
        </>
      ) : timeBench ? (
        <>
          <p className="font-heading font-black text-2xl text-acid mt-2">{fmtTime(pr.best_time_sec)}</p>
          <p className="mono-label mt-1">best time{pr.best_score ? ` · ${pr.best_score}` : ""}</p>
        </>
      ) : amrap ? (
        <>
          <p className="font-heading font-black text-2xl text-acid mt-2">{pr.best_total_reps}<span className="text-sm text-[#8A91A6] ml-1">reps</span></p>
          <p className="mono-label mt-1">{pr.best_score || "AMRAP"}</p>
        </>
      ) : (
        <p className="font-heading font-black text-2xl text-acid mt-2">{pr.best_score || "—"}</p>
      )}
      <p className="mono-label mt-1">{pr.sessions} session{pr.sessions === 1 ? "" : "s"}</p>
    </div>
  );
}

function LiftRow({ l }) {
  const detail = [];
  if (l.weight_kg != null) detail.push(`${l.weight_kg}kg`);
  if (l.reps != null) detail.push(`${l.sets ? l.sets + "x" : ""}${l.reps}`);
  if (l.time_sec != null) detail.push(fmtTime(l.time_sec));
  else if (l.score) detail.push(l.score);
  if (l.distance_m != null) detail.push(`${l.distance_m}m`);
  return (
    <div className="flex items-center justify-between border-b border-[#1E222A] py-2 text-sm" data-testid="lift-row">
      <div className="min-w-0">
        <span className="text-white/90">{l.movement}</span>
        {l.is_pr && <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-black bg-acid rounded px-1.5 py-0.5"><Trophy className="w-3 h-3" />PR</span>}
        <span className="text-[#8A91A6] ml-2 font-mono2 text-xs">{detail.join(" · ")}</span>
        {l.source === "voice" && <span className="text-[#565C6F] ml-2 text-[10px] uppercase">voice</span>}
      </div>
      <span className="mono-label whitespace-nowrap">{l.date?.slice(0, 10)}</span>
    </div>
  );
}

function MovementTrend({ movements }) {
  const strengthMoves = useMemo(() => movements.filter((m) => m.best_weight_kg != null), [movements]);
  const [sel, setSel] = useState("");
  const active = sel || strengthMoves[0]?.movement || "";
  const { data: pts = [] } = useQuery({
    queryKey: ["mseries", active],
    queryFn: async () => (await api.get("/api/logs/movement-series", { params: { movement: active } })).data,
    enabled: !!active,
  });

  return (
    <div className="surface rounded-lg p-5" data-testid="movement-trend">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Activity className="w-4 h-4 text-acid" />
        <span className="font-heading font-bold tracking-tight">Movement trend</span>
        {strengthMoves.length > 0 && (
          <Select value={active} onValueChange={setSel}>
            <SelectTrigger className="w-52 bg-[#0F1115] border-[#1E222A] h-9 ml-auto" data-testid="movement-select"><SelectValue placeholder="Pick a movement…" /></SelectTrigger>
            <SelectContent className="surface border-[#1E222A]">
              {strengthMoves.map((m) => <SelectItem key={m.movement} value={m.movement}>{m.movement}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      {strengthMoves.length === 0 ? (
        <p className="text-[#565C6F] text-sm py-8 text-center">Log a weighted lift to chart its trajectory over time.</p>
      ) : pts.length > 1 ? (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={pts} margin={{ left: -18, right: 6, top: 6 }}>
              <CartesianGrid stroke="#1E222A" vertical={false} />
              <XAxis dataKey="date" stroke="#565C6F" fontSize={11} tickLine={false} />
              <YAxis stroke="#565C6F" fontSize={11} domain={["auto", "auto"]} tickLine={false} />
              <Tooltip contentStyle={{ background: "#0F1115", border: "1px solid #1E222A", borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="weight_kg" name="Weight (kg)" stroke="#CCFF00" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="est_1rm" name="Est 1RM (kg)" stroke="#FFB340" strokeWidth={2} strokeDasharray="4 3" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="text-[#565C6F] text-sm py-8 text-center">Log <span className="text-white/90">{active}</span> again to see its trend line.</p>
      )}
    </div>
  );
}

function DevelopmentAreas({ hasLogs }) {
  const { data, isFetching } = useQuery({
    queryKey: ["insights"],
    queryFn: async () => (await api.get("/api/logs/insights")).data,
    enabled: hasLogs,
  });
  const areas = data?.areas || [];
  if (!hasLogs) return null;
  return (
    <div className="surface rounded-lg p-5" data-testid="development-areas">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-4 h-4 text-neural" />
        <span className="font-heading font-bold tracking-tight">Areas for development</span>
        <span className="mono-label ml-auto">OCTON · Bayesian</span>
      </div>
      {isFetching && areas.length === 0 ? (
        <p className="mono-label animate-pulse">OCTON is reading your patterns…</p>
      ) : areas.length === 0 ? (
        <p className="text-[#565C6F] text-sm">Log a few more sessions and OCTON will surface your development areas.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {areas.map((a) => (
            <div key={a.title} className="rounded-md border border-[#1E222A] surface-elev p-3" data-testid="dev-area">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium text-white/90">{a.title}</p>
                <span className="mono-label shrink-0" style={{ color: (a.confidence ?? 0) > 0.7 ? "#FF3B30" : "#CCFF00" }}>
                  {Math.round((a.confidence ?? 0) * 100)}%
                </span>
              </div>
              <p className="text-xs text-[#8A91A6] mt-1.5">{a.insight}</p>
              {a.recommendation && <p className="text-xs text-acid mt-1.5 flex items-start gap-1"><Timer className="w-3 h-3 mt-0.5 shrink-0" />{a.recommendation}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent = "#fff" }) {
  return (
    <div className="surface rounded-lg p-5">
      <Icon className="w-5 h-5 text-[#565C6F] mb-3" />
      <p className="font-heading font-black text-2xl" style={{ color: accent }}>{value}</p>
      <p className="mono-label mt-1">{label}</p>
    </div>
  );
}

function AddMeasurement({ onDone }) {
  const [open, setOpen] = useState(false);
  const [weight, setWeight] = useState("");
  const [bf, setBf] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const metrics = {};
      if (weight) metrics.weight_kg = Number(weight);
      if (bf) metrics.body_fat = Number(bf);
      await api.post("/api/progress", { type: "measurement", metrics });
      toast.success("Measurement logged"); setOpen(false); setWeight(""); setBf(""); onDone?.();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-acid text-black hover:bg-[#AACC00] rounded-full" data-testid="add-measurement-btn"><Plus className="w-4 h-4" /> Add measurement</Button>
      </DialogTrigger>
      <DialogContent className="surface border-[#1E222A]">
        <DialogHeader><DialogTitle className="font-heading">Log measurement</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="mono-label">Weight (kg)</Label><Input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} className="mt-1.5 bg-[#0F1115] border-[#1E222A]" data-testid="weight-input" /></div>
          <div><Label className="mono-label">Body fat (%)</Label><Input type="number" value={bf} onChange={(e) => setBf(e.target.value)} className="mt-1.5 bg-[#0F1115] border-[#1E222A]" /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy || !weight} className="bg-acid text-black hover:bg-[#AACC00]" data-testid="submit-measurement-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
